import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useAdminSecretStore } from '../stores/adminSecret';
import { useAuthStore } from '../stores/auth';
import { validateAdminSecret } from '../lib/api';
import { Spinner } from './ui/Spinner';

/**
 * Gate for platform-admin pages. /v1/admin/* requires the x-admin-secret header
 * (a JWT with role=admin is NOT sufficient — see PlatformAdminGuard), so without
 * this the admin section just 403s on every request. The gate collects the secret
 * once per tab, validates it against the backend, and self-heals stale secrets.
 */
export function AdminSecretGate({ children }: { children: ReactNode }) {
  const secret = useAdminSecretStore((s) => s.secret);
  const setSecret = useAdminSecretStore((s) => s.setSecret);
  const clear = useAdminSecretStore((s) => s.clear);
  const token = useAuthStore((s) => s.token);

  const [status, setStatus] = useState<'checking' | 'need' | 'ok'>(secret ? 'checking' : 'need');
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Validate a stored secret on mount; drop it and re-prompt if the backend rejects it.
  useEffect(() => {
    if (!secret) {
      setStatus('need');
      return;
    }
    if (!token) return;

    let cancelled = false;
    setStatus('checking');
    validateAdminSecret(token, secret)
      .then((ok) => {
        if (cancelled) return;
        if (ok) {
          setStatus('ok');
        } else {
          clear();
          setStatus('need');
        }
      })
      .catch(() => {
        // Network/other error — don't lock the operator out over a transient failure.
        if (!cancelled) setStatus('ok');
      });

    return () => {
      cancelled = true;
    };
  }, [secret, token, clear]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const candidate = value.trim();
    if (!token || !candidate) return;

    setSubmitting(true);
    setError('');
    try {
      const ok = await validateAdminSecret(token, candidate);
      if (ok) {
        setSecret(candidate);
        setValue('');
        setStatus('ok');
      } else {
        setError('Invalid admin secret.');
      }
    } catch {
      setError('Could not verify the secret. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (status === 'ok') return <>{children}</>;

  if (status === 'checking') {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto mt-10 w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900 p-8">
      <h1 className="text-lg font-semibold text-zinc-100">Platform admin access</h1>
      <p className="mt-1 text-sm text-zinc-400">
        These pages expose cross-tenant data and require the platform admin secret. It is kept only for this browser tab.
      </p>

      {error ? (
        <div className="mt-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300" role="alert">
          {error}
        </div>
      ) : null}

      <form onSubmit={(e) => void handleSubmit(e)} className="mt-5 space-y-4">
        <div>
          <label htmlFor="admin-secret" className="mb-1.5 block text-sm font-medium text-zinc-300">
            Admin secret
          </label>
          <input
            id="admin-secret"
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="x-admin-secret"
            className="ub-auth-input w-full"
            autoFocus
          />
        </div>
        <button
          type="submit"
          className="ub-btn ub-btn-primary w-full justify-center"
          disabled={submitting || !value.trim()}
        >
          {submitting ? <Spinner /> : null}
          Unlock admin
        </button>
      </form>
    </div>
  );
}
